import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Add } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { Grid } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { MultiSelectValueViewerProps } from './MultiSelectValueViewer.props';
import StyledWrapper from './MultiSelectValueViewer.styles';
import { NEGATIVE_PREFIX } from '../../pages/Financials/financials-widget-drawer/types';
import { useUpdateAttributeMapping } from '../../services/hooks/attributeMapper';
import { SheetAttributeMapping } from '../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import SheetAttributeManager from '../../services/sheet-attribute-manager/SheetAttributeManager';
import { useAttributeMapperStore } from '../../store/useAttributeMapperStore';
import convertCamelCaseToSnakeCase from '../../utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import logger from '../../utilities/logger';
import AISuggestionChip from '../AISuggestionChip';
import TextCell from '../SelectValueViewer/TextCell/TextCell';

const ICON_CONTAINER_WIDTH = 20 + 10; // Icon + Padding

const MultiSelectValueViewer = ({ value, cell, onDoubleClick, options }: MultiSelectValueViewerProps) => {
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const { attributeMapperManager, setAttributeMapperManager } = useAttributeMapperStore();
  const [updateMapping] = useUpdateAttributeMapping();

  const values = Array.isArray(cell.value) ? cell.value : undefined;

  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [selected, setSelected] = useState(values);

  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const shouldDisplayTooltip = useMemo(
    () => textWidth + ICON_CONTAINER_WIDTH >= containerWidth,
    [textWidth, containerWidth]
  );

  useEffect(() => {
    setSelected(options?.map(option => option?.label as string) ?? []);
  }, [cell, values, options]);

  let className = 'value-viewer';

  const id = useMemo(() => {
    let key = '';
    if (cell?.getFullKey) {
      key = cell.getFullKey();
    } else if (cell?.key) {
      key = `${selected || ''}-${cell.key}`;
    } else {
      key = selected?.join(',') || '';
    }
    return `select-value-viewer-${key}`;
  }, [cell, selected]);

  // Custom double click handler for cells not in a spreadsheet component
  const handleDoubleClick = useCallback(() => {
    const event = new MouseEvent('dblclick', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    document.getElementById(id)?.dispatchEvent(event);
  }, [id]);

  useLayoutEffect(() => {
    if (containerRef?.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (textRef?.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, [containerRef, textRef]);

  // Helper: Extract column/row key and type from cell
  const getCellLocationInfo = useCallback(() => {
    let columnOrRowKey: string | undefined;
    let type: 'column' | 'row' | undefined;

    if (cell.colAlias) {
      columnOrRowKey = cell.colAlias;
      type = 'column';
    } else if (cell.col !== undefined && cell.col !== null) {
      columnOrRowKey = cell.col.toString();
      type = 'column';
    } else if (cell.row !== undefined && cell.row !== null) {
      columnOrRowKey = cell.row.toString();
      type = 'row';
    } else if (cell.rowNumber !== undefined && cell.rowNumber !== null) {
      columnOrRowKey = cell.rowNumber.toString();
      type = 'row';
    }

    return { columnOrRowKey, type };
  }, [cell]);

  // Helper: Update backend and store with modified mapping
  const updateMappingBackendAndStore = useCallback(
    async (updatedMappingData: SheetAttributeMapping) => {
      const mappingId = updatedMappingData.id;

      if (mappingId) {
        const backendPayload = convertCamelCaseToSnakeCase(updatedMappingData);
        await updateMapping(mappingId, backendPayload);
        logger.info('MultiSelectValueViewer', `Updated mapping ${mappingId} in backend`);
      } else {
        logger.info('MultiSelectValueViewer', 'No mapping ID found, skipping backend update');
      }

      setAttributeMapperManager(new SheetAttributeManager(updatedMappingData));
    },
    [updateMapping, setAttributeMapperManager]
  );

  const handleOrdinateAccept = useCallback(
    async (ordinateValue: string) => {
      try {
        if (!attributeMapperManager) {
          logger.error('MultiSelectValueViewer', 'No attribute mapper manager found');
          return;
        }

        const { columnOrRowKey, type } = getCellLocationInfo();
        logger.info('MultiSelectValueViewer', `Accepting ordinate: value=${ordinateValue}, key=${columnOrRowKey}, type=${type}`);

        const wasAccepted = attributeMapperManager.acceptOrdinateByValue(ordinateValue, columnOrRowKey, type);

        if (!wasAccepted) {
          logger.info('MultiSelectValueViewer', `Could not find ordinate with value: ${ordinateValue}`);
          enqueueSnackbar('Mapping not found', { variant: 'warning' });
          return;
        }

        const updatedMappingData = attributeMapperManager.sheetAttributeMapping;
        await updateMappingBackendAndStore(updatedMappingData);

        // Trigger a re-render by updating the cell (just forces refresh, no actual change)
        if (onCellsChanged) {
          await onCellsChanged([{ cell, value: values }]);
        }

        enqueueSnackbar('Mapping accepted', { variant: 'success' });
      } catch (error) {
        logger.error('MultiSelectValueViewer - Error accepting ordinate', error);
        enqueueSnackbar('Failed to accept mapping', { variant: 'error' });
      }
    },
    [attributeMapperManager, getCellLocationInfo, updateMappingBackendAndStore, onCellsChanged, cell, values]
  );

  const handleOrdinateDecline = useCallback(
    async (ordinateValue: string) => {
      try {
        if (!attributeMapperManager) {
          logger.error('MultiSelectValueViewer', 'No attribute mapper manager found');
          return;
        }

        const { columnOrRowKey, type } = getCellLocationInfo();
        logger.info('MultiSelectValueViewer', `Declining ordinate: value=${ordinateValue}, key=${columnOrRowKey}, type=${type}`);

        const wasDeclined = attributeMapperManager.declineOrdinateByValue(ordinateValue, columnOrRowKey, type);

        if (!wasDeclined) {
          logger.info('MultiSelectValueViewer', `Could not find ordinate with value: ${ordinateValue}`);
          enqueueSnackbar('Mapping not found', { variant: 'warning' });
          return;
        }

        const updatedMappingData = attributeMapperManager.sheetAttributeMapping;
        await updateMappingBackendAndStore(updatedMappingData);

        // Remove the value from the cell
        const newValues = values?.filter(v => v !== ordinateValue);
        if (onCellsChanged) {
          await onCellsChanged([{ cell, value: newValues }]);
        }

        enqueueSnackbar('Mapping declined', { variant: 'success' });
      } catch (error) {
        logger.error('MultiSelectValueViewer - Error declining ordinate', error);
        enqueueSnackbar('Failed to decline mapping', { variant: 'error' });
      }
    },
    [attributeMapperManager, getCellLocationInfo, updateMappingBackendAndStore, onCellsChanged, cell, values]
  );

  if (cell.readOnly) {
    return (
      <Grid component="span" className={className} ref={containerRef}>
        <TextCell textRef={textRef} shouldDisplayTooltip={shouldDisplayTooltip}>
          {selected}
        </TextCell>
      </Grid>
    );
  }

  className = 'select-value-viewer';

  return (
    <Grid
      className={className}
      id={id}
      onClick={onDoubleClick || handleDoubleClick}
      onKeyDown={onDoubleClick}
      ref={containerRef}
      role="button"
      spacing={2}
      tabIndex={0}>
      <StyledWrapper direction="row" spacing={0.25}>
        <>
          {values?.map(value => {
            const option = options?.find(option => option?.value === value);
            if (!option) {
              return null;
            }

            const { ordinate } = option;
            const isAiGenerated = ordinate?.isAiGenerated;
            const isAccepted = ordinate?.isAccepted;
            const isNegative = value.startsWith(NEGATIVE_PREFIX);

            // Show AI suggestion chip if AI-generated and not accepted
            if (isAiGenerated && !isAccepted) {
              return (
                <AISuggestionChip
                  key={value}
                  label={option.label as string}
                  value={value}
                  commentary={ordinate?.commentary}
                  confidence={ordinate?.confidence}
                  color={isNegative ? 'error' : 'primary'}
                  onAccept={() => handleOrdinateAccept(value)}
                  onDecline={() => handleOrdinateDecline(value)}
                />
              );
            }

            // Regular chip for accepted or non-AI mappings
            return (
              <Chip
                color={isNegative ? 'error' : 'primary'}
                key={value}
                label={option?.label}
                variant="filled"
                onDelete={async e => {
                  e.stopPropagation();
                  if (onCellsChanged) {
                    await onCellsChanged([{ cell, value: values?.filter(v => v !== value) }]);
                  }
                }}
                sx={{
                  height: 24,
                  '& .MuiChip-label': {
                    fontSize: '0.8125rem',
                  },
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(25, 118, 210, 0.7)',
                    fill: '#ffffff',
                    width: '14px',
                    '&:hover': {
                      color: 'rgba(25, 118, 210, 1)',
                    },
                  },
                }}
              />
            );
          })}
        </>
      </StyledWrapper>
      <Grid component="span" className="dropdown-icon" sx={{}}>
        <Add />
      </Grid>
    </Grid>
  );
};

export default MultiSelectValueViewer;
